import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Plus, Pencil, Trash2, LogOut, Loader2, Save, X, Eye, EyeOff, Users, Mic, RefreshCw, Instagram, ShoppingBag, ChevronUp, ChevronDown
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: '',
  description: '',
  youtube_url: '',
  spotify_url: '',
  episode_number: 1,
  thumbnail_url: '',
  video_url: '',
  reels: [],
  is_published: true,
};

export default function AdminPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [episodes, setEpisodes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [igClips, setIgClips] = useState([]);
  const [merchProducts, setMerchProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('episodes');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [newReelUrl, setNewReelUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [merchDialogOpen, setMerchDialogOpen] = useState(false);
  const [editingMerchId, setEditingMerchId] = useState(null);
  const [merchForm, setMerchForm] = useState({ name: '', price: 0, description: '', category: 'apparel', images: [], is_coming_soon: true, is_visible: true, display_order: 0 });
  const [merchSaving, setMerchSaving] = useState(false);
  const [merchUploading, setMerchUploading] = useState(false);

  const handleMerchImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMerchUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/upload-image`, fd, getAuthHeaders());
      setMerchForm((f) => ({ ...f, images: [...(f.images || []).filter(Boolean), data.image_url].slice(0, 4) }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Image upload failed');
    } finally { setMerchUploading(false); }
  };

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    };
  }, []);

  const fetchEpisodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/episodes`, getAuthHeaders());
      setEpisodes(data || []);
    } catch (err) {
      console.error('Failed to fetch episodes:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchSubscribers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, getAuthHeaders());
      setSubscribers(data || []);
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    }
  }, [getAuthHeaders]);

  const fetchIgClips = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/instagram-clips`);
      setIgClips(data || []);
    } catch (err) {
      console.error('Failed to fetch IG clips:', err);
    }
  }, []);

  const fetchMerch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/merch`, getAuthHeaders());
      setMerchProducts(data || []);
    } catch (err) {
      console.error('Failed to fetch merch:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!authLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
      return;
    }
    if (user && user.role === 'admin') {
      fetchEpisodes();
      fetchSubscribers();
      fetchIgClips();
      fetchMerch();
    }
  }, [user, authLoading, navigate, fetchEpisodes, fetchSubscribers, fetchIgClips, fetchMerch]);

  const openCreate = () => {
    setEditingId(null);
    const nextNum = episodes.length > 0
      ? Math.max(...episodes.map(e => e.episode_number)) + 1
      : 1;
    setForm({ ...emptyForm, episode_number: nextNum });
    setDialogOpen(true);
  };

  const openEdit = (ep) => {
    setEditingId(ep.id);
    setForm({
      title: ep.title || '',
      description: ep.description || '',
      youtube_url: ep.youtube_url || '',
      spotify_url: ep.spotify_url || '',
      episode_number: ep.episode_number || 1,
      thumbnail_url: ep.thumbnail_url || '',
      video_url: ep.video_url || '',
      reels: ep.reels || [],
      is_published: ep.is_published !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, reels: (form.reels || []).filter(r => r.link && r.link.trim()) };
      if (editingId) {
        await axios.put(`${API}/admin/episodes/${editingId}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/episodes`, payload, getAuthHeaders());
      }
      setDialogOpen(false);
      fetchEpisodes();
    } catch (err) {
      console.error('Save failed:', err);
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/episodes/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchEpisodes();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteSubscriber = async (id) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, getAuthHeaders());
      fetchSubscribers();
    } catch (err) {
      console.error('Delete subscriber failed:', err);
    }
  };

  const handleSyncSpotify = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/sync-spotify`, {}, getAuthHeaders());
      setSyncResult(data);
      if (data.new_episodes > 0) {
        fetchEpisodes();
      }
    } catch (err) {
      setSyncResult({ error: err.response?.data?.detail || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleAddClip = async () => {
    if (!newReelUrl.trim()) return;
    try {
      await axios.post(`${API}/admin/instagram-clips`, {
        reel_url: newReelUrl,
        order: igClips.length + 1
      }, getAuthHeaders());
      setNewReelUrl('');
      fetchIgClips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add clip');
    }
  };

  const handleDeleteClip = async (id) => {
    try {
      await axios.delete(`${API}/admin/instagram-clips/${id}`, getAuthHeaders());
      fetchIgClips();
    } catch (err) {
      console.error('Delete clip failed:', err);
    }
  };

  const openCreateMerch = () => {
    setEditingMerchId(null);
    setMerchForm({ name: '', price: 0, description: '', category: 'apparel', images: [], is_coming_soon: true, is_visible: true, display_order: merchProducts.length + 1 });
    setMerchDialogOpen(true);
  };

  const openEditMerch = (p) => {
    setEditingMerchId(p.id);
    setMerchForm({ name: p.name||'', price: p.price||0, description: p.description||'', category: p.category||'apparel', images: p.images||[], is_coming_soon: p.is_coming_soon!==false, is_visible: p.is_visible!==false, display_order: p.display_order||0 });
    setMerchDialogOpen(true);
  };

  const handleSaveMerch = async () => {
    setMerchSaving(true);
    const payload = { ...merchForm, images: (merchForm.images || []).map((i) => i.trim()).filter(Boolean) };
    try {
      if (editingMerchId) {
        await axios.put(`${API}/admin/merch/${editingMerchId}`, payload, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/merch`, payload, getAuthHeaders());
      }
      setMerchDialogOpen(false);
      fetchMerch();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save product');
    } finally { setMerchSaving(false); }
  };

  const handleDeleteMerch = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/merch/${id}`, getAuthHeaders());
      fetchMerch();
    } catch (err) { console.error('Delete merch failed:', err); }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.post(`${API}/admin/upload-video`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
        timeout: 300000,
      });
      setForm(prev => ({ ...prev, video_url: data.video_url }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Video upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (authLoading) {
    return (
      <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#FF2A2A]" />
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="pt-16 md:pt-20 min-h-screen">
      {/* Header Bar */}
      <div className="border-b border-white/5 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A]">
              ADMIN PANEL
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tight">
              {{ episodes: 'MANAGE EPISODES', subscribers: 'SUBSCRIBERS', instagram: 'INSTAGRAM CLIPS', merch: 'MANAGE MERCH' }[activeTab]}
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Tab Buttons */}
            <div data-testid="admin-tab-row" className="flex w-full sm:w-auto overflow-x-auto items-center border border-white/10 sm:mr-2">
              <button
                data-testid="admin-tab-episodes"
                onClick={() => setActiveTab('episodes')}
                className={`flex shrink-0 items-center gap-1.5 px-4 py-2 font-body text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'episodes' ? 'bg-[#FF2A2A] text-white' : 'text-[#71717A] hover:text-white'
                }`}
              >
                <Mic size={14} /> Episodes
              </button>
              <button
                data-testid="admin-tab-subscribers"
                onClick={() => setActiveTab('subscribers')}
                className={`flex shrink-0 items-center gap-1.5 px-4 py-2 font-body text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'subscribers' ? 'bg-[#FF2A2A] text-white' : 'text-[#71717A] hover:text-white'
                }`}
              >
                <Users size={14} /> Subs ({subscribers.length})
              </button>
              <button
                data-testid="admin-tab-instagram"
                onClick={() => setActiveTab('instagram')}
                className={`flex shrink-0 items-center gap-1.5 px-4 py-2 font-body text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'instagram' ? 'bg-[#E1306C] text-white' : 'text-[#71717A] hover:text-white'
                }`}
              >
                <Instagram size={14} /> IG ({igClips.length})
              </button>
              <button
                data-testid="admin-tab-merch"
                onClick={() => setActiveTab('merch')}
                className={`flex shrink-0 items-center gap-1.5 px-4 py-2 font-body text-xs font-bold uppercase tracking-wider transition-colors ${
                  activeTab === 'merch' ? 'bg-[#FF2A2A] text-white' : 'text-[#71717A] hover:text-white'
                }`}
              >
                <ShoppingBag size={14} /> Merch ({merchProducts.length})
              </button>
            </div>
            {activeTab === 'merch' && (
              <Button
                data-testid="admin-add-merch-btn"
                onClick={openCreateMerch}
                className="bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#E60000] transition-all px-6"
              >
                <Plus size={16} className="mr-2" /> ADD PRODUCT
              </Button>
            )}
            {activeTab === 'episodes' && (
              <>
              <Button
                data-testid="admin-sync-spotify-btn"
                onClick={handleSyncSpotify}
                disabled={syncing}
                variant="outline"
                className="border-[#1DB954]/50 text-[#1DB954] font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#1DB954]/10 transition-all px-4"
              >
                <RefreshCw size={16} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'SYNCING...' : 'SYNC SPOTIFY'}
              </Button>
              <Button
                data-testid="admin-add-episode-btn"
                onClick={openCreate}
                className="bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#E60000] transition-all px-6"
              >
                <Plus size={16} className="mr-2" /> ADD EPISODE
              </Button>
              </>
            )}
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="border-white/10 text-white rounded-none hover:bg-white/5 font-body"
            >
              <LogOut size={16} />
            </Button>
          </div>
        </div>
      </div>

      {/* Sync Result Banner */}
      {syncResult && (
        <div className={`max-w-7xl mx-auto px-4 md:px-8 pt-4`}>
          <div className={`p-4 border ${syncResult.error ? 'bg-[#FF2A2A]/10 border-[#FF2A2A]/30' : 'bg-[#1DB954]/10 border-[#1DB954]/30'} flex items-center justify-between`} data-testid="sync-result-banner">
            <p className="font-body text-sm">
              {syncResult.error ? (
                <span className="text-[#FF2A2A]">Sync failed: {syncResult.error}</span>
              ) : syncResult.new_episodes > 0 ? (
                <span className="text-[#1DB954]">
                  Synced {syncResult.new_episodes} new episode{syncResult.new_episodes > 1 ? 's' : ''} from Spotify: {syncResult.added?.join(', ')}
                </span>
              ) : (
                <span className="text-[#A1A1AA]">All episodes are up to date ({syncResult.total_on_spotify} on Spotify)</span>
              )}
            </p>
            <button onClick={() => setSyncResult(null)} className="text-[#71717A] hover:text-white ml-4">
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Episodes Table */}
      {activeTab === 'episodes' && (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-[#FF2A2A]" />
          </div>
        ) : episodes.length === 0 ? (
          <div className="text-center py-20 bg-[#141414] border border-white/5" data-testid="admin-no-episodes">
            <p className="font-heading text-2xl uppercase mb-3">NO EPISODES YET</p>
            <p className="font-body text-sm text-[#71717A] mb-6">Create your first episode to get started.</p>
            <Button
              data-testid="admin-add-first-episode-btn"
              onClick={openCreate}
              className="bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#E60000] px-8"
            >
              <Plus size={16} className="mr-2" /> ADD FIRST EPISODE
            </Button>
          </div>
        ) : (
          <div className="bg-[#141414] border border-white/5 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">#</TableHead>
                  <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">Title</TableHead>
                  <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold hidden md:table-cell">YouTube</TableHead>
                  <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold hidden md:table-cell">Status</TableHead>
                  <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {episodes.map((ep) => (
                  <TableRow
                    key={ep.id}
                    data-testid={`admin-episode-row-${ep.id}`}
                    className="border-white/5 hover:bg-white/[0.02]"
                  >
                    <TableCell className="font-body font-bold text-[#FF2A2A]">
                      {ep.episode_number}
                    </TableCell>
                    <TableCell className="font-body font-semibold text-white max-w-[200px] truncate">
                      {ep.title}
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-body text-sm text-[#71717A] max-w-[200px] truncate">
                      {ep.youtube_url || '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {ep.is_published ? (
                        <span className="inline-flex items-center gap-1 text-xs font-body font-bold text-green-400">
                          <Eye size={12} /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-body font-bold text-[#71717A]">
                          <EyeOff size={12} /> Draft
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          data-testid={`admin-edit-${ep.id}`}
                          onClick={() => openEdit(ep)}
                          variant="ghost"
                          size="sm"
                          className="text-white hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"
                        >
                          <Pencil size={14} />
                        </Button>
                        {deleteConfirm === ep.id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              data-testid={`admin-confirm-delete-${ep.id}`}
                              onClick={() => handleDelete(ep.id)}
                              variant="ghost"
                              size="sm"
                              className="text-[#FF2A2A] hover:bg-[#FF2A2A]/10 rounded-none h-8 px-2 text-xs font-body font-bold"
                            >
                              YES
                            </Button>
                            <Button
                              onClick={() => setDeleteConfirm(null)}
                              variant="ghost"
                              size="sm"
                              className="text-[#71717A] hover:bg-white/5 rounded-none h-8 px-2 text-xs font-body"
                            >
                              NO
                            </Button>
                          </div>
                        ) : (
                          <Button
                            data-testid={`admin-delete-${ep.id}`}
                            onClick={() => setDeleteConfirm(ep.id)}
                            variant="ghost"
                            size="sm"
                            className="text-[#71717A] hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {subscribers.length === 0 ? (
            <div className="text-center py-20 bg-[#141414] border border-white/5" data-testid="admin-no-subscribers">
              <Users size={48} className="mx-auto text-[#FF2A2A]/30 mb-4" />
              <p className="font-heading text-2xl uppercase mb-3">NO SUBSCRIBERS YET</p>
              <p className="font-body text-sm text-[#71717A]">When fans subscribe, they'll show up here.</p>
            </div>
          ) : (
            <div className="bg-[#141414] border border-white/5 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">Email</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">Name</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold hidden md:table-cell">Subscribed</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((sub) => (
                    <TableRow key={sub.id} data-testid={`admin-subscriber-row-${sub.id}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-body font-semibold text-white">{sub.email}</TableCell>
                      <TableCell className="font-body text-sm text-[#A1A1AA]">{sub.name || '—'}</TableCell>
                      <TableCell className="hidden md:table-cell font-body text-sm text-[#71717A]">
                        {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          data-testid={`admin-delete-sub-${sub.id}`}
                          onClick={() => handleDeleteSubscriber(sub.id)}
                          variant="ghost"
                          size="sm"
                          className="text-[#71717A] hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-4 py-3 border-t border-white/5">
                <p className="font-body text-xs text-[#71717A]">{subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''} total</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instagram Tab */}
      {activeTab === 'instagram' && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {/* Add New Clip */}
          <div className="bg-[#141414] border border-white/5 p-6 mb-6" data-testid="ig-add-clip-form">
            <h3 className="font-heading text-lg uppercase mb-4">ADD INSTAGRAM REEL</h3>
            <div className="flex gap-3">
              <Input
                data-testid="ig-reel-url-input"
                value={newReelUrl}
                onChange={(e) => setNewReelUrl(e.target.value)}
                placeholder="https://www.instagram.com/reel/XXXXX/"
                className="flex-1 bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#E1306C] placeholder:text-[#71717A]"
              />
              <Button
                data-testid="ig-add-clip-btn"
                onClick={handleAddClip}
                disabled={!newReelUrl.trim()}
                className="bg-[#E1306C] text-white font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#C1185B] px-6 disabled:opacity-50"
              >
                <Plus size={16} className="mr-2" /> ADD
              </Button>
            </div>
          </div>

          {/* Clips Grid */}
          {igClips.length === 0 ? (
            <div className="text-center py-20 bg-[#141414] border border-white/5" data-testid="ig-no-clips">
              <Instagram size={48} className="mx-auto text-[#E1306C]/30 mb-4" />
              <p className="font-heading text-2xl uppercase mb-3">NO CLIPS YET</p>
              <p className="font-body text-sm text-[#71717A]">Paste an Instagram reel URL above to add your first clip.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {igClips.map((clip, idx) => (
                <div key={clip.id} data-testid={`ig-admin-clip-${clip.reel_id}`} className="bg-[#141414] border border-white/5 overflow-hidden group relative">
                  <iframe
                    src={`https://www.instagram.com/reel/${clip.reel_id}/embed/`}
                    width="100%"
                    height="400"
                    frameBorder="0"
                    scrolling="no"
                    allowTransparency="true"
                    allow="encrypted-media"
                    title={`Reel ${idx + 1}`}
                    className="w-full pointer-events-none"
                  />
                  <div className="p-3 flex items-center justify-between border-t border-white/5">
                    <div>
                      <p className="font-body text-xs text-[#71717A]">Reel #{idx + 1}</p>
                      <p className="font-body text-xs text-[#A1A1AA] truncate max-w-[180px]">{clip.reel_id}</p>
                    </div>
                    <Button
                      data-testid={`ig-delete-clip-${clip.reel_id}`}
                      onClick={() => handleDeleteClip(clip.id)}
                      variant="ghost"
                      size="sm"
                      className="text-[#71717A] hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Merch Tab */}
      {activeTab === 'merch' && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {merchProducts.length === 0 ? (
            <div className="text-center py-20 bg-[#141414] border border-white/5" data-testid="admin-no-merch">
              <ShoppingBag size={48} className="mx-auto text-[#FF2A2A]/30 mb-4" />
              <p className="font-heading text-2xl uppercase mb-3">NO PRODUCTS YET</p>
              <p className="font-body text-sm text-[#71717A] mb-6">Add your first merch product.</p>
              <Button onClick={openCreateMerch} className="bg-[#FF2A2A] text-white font-body font-bold uppercase tracking-wide rounded-none hover:bg-[#E60000] px-8">
                <Plus size={16} className="mr-2" /> ADD PRODUCT
              </Button>
            </div>
          ) : (
            <div className="bg-[#141414] border border-white/5 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold w-12">#</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">Product</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold">Price</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold hidden md:table-cell">Images</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold hidden md:table-cell">Status</TableHead>
                    <TableHead className="font-body text-xs uppercase tracking-wider text-[#71717A] font-bold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchProducts.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-merch-row-${p.id}`} className="border-white/5 hover:bg-white/[0.02]">
                      <TableCell className="font-body font-bold text-[#FF2A2A]">{p.display_order}</TableCell>
                      <TableCell className="font-body font-semibold text-white max-w-[110px] sm:max-w-[200px] truncate">{p.name}</TableCell>
                      <TableCell className="font-body text-sm text-[#A1A1AA]">${p.price.toFixed(2)}</TableCell>
                      <TableCell className="hidden md:table-cell font-body text-sm text-[#71717A]">{(p.images||[]).length} img</TableCell>
                      <TableCell className="hidden md:table-cell">
                        <span className={`text-xs font-body font-bold ${p.is_coming_soon ? 'text-[#FF2A2A]' : 'text-green-400'}`}>{p.is_coming_soon ? 'Coming Soon' : 'Live'}</span>
                        {!p.is_visible && (
                          <span className="ml-2 text-xs font-body font-bold text-[#71717A]"><EyeOff size={12} className="inline mr-1" />Hidden</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button data-testid={`admin-edit-merch-${p.id}`} onClick={() => openEditMerch(p)} variant="ghost" size="sm"
                            className="text-white hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"><Pencil size={14} /></Button>
                          <Button data-testid={`admin-delete-merch-${p.id}`} onClick={() => handleDeleteMerch(p.id)} variant="ghost" size="sm"
                            className="text-[#71717A] hover:text-[#FF2A2A] hover:bg-white/5 rounded-none h-8 w-8 p-0"><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Merch Dialog */}
      <Dialog open={merchDialogOpen} onOpenChange={setMerchDialogOpen}>
        <DialogContent className="bg-[#141414] border border-white/10 text-white max-w-lg rounded-none sm:max-w-xl" data-testid="merch-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              {editingMerchId ? 'EDIT PRODUCT' : 'NEW PRODUCT'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Name</Label>
                <Input data-testid="merch-name-input" value={merchForm.name} onChange={(e) => setMerchForm({...merchForm, name: e.target.value})}
                  placeholder="Product name" className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]" />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Price ($)</Label>
                <Input data-testid="merch-price-input" type="number" step="0.01" value={merchForm.price} onChange={(e) => setMerchForm({...merchForm, price: parseFloat(e.target.value)||0})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Description</Label>
              <Textarea data-testid="merch-desc-input" value={merchForm.description} onChange={(e) => setMerchForm({...merchForm, description: e.target.value})}
                placeholder="Short description" rows={2} className="bg-[#0A0A0A] border-white/10 text-white rounded-none resize-none focus:border-[#FF2A2A]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Category</Label>
                <select value={merchForm.category} onChange={(e) => setMerchForm({...merchForm, category: e.target.value})}
                  className="w-full appearance-none bg-[#0A0A0A] border border-white/10 text-white font-body text-sm px-3 py-2 rounded focus:border-[#FF2A2A] focus:outline-none">
                  <option value="apparel">Apparel</option>
                  <option value="accessory">Accessory</option>
                  <option value="drinkware">Drinkware</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Order</Label>
                <Input type="number" value={merchForm.display_order} onChange={(e) => setMerchForm({...merchForm, display_order: parseInt(e.target.value)||0})}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]" />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Status</Label>
                <div className="flex flex-col gap-1.5 mt-1">
                  <button type="button" onClick={() => setMerchForm({...merchForm, is_coming_soon: !merchForm.is_coming_soon})}
                    className={`flex items-center gap-1.5 px-2 py-1 font-body text-[10px] font-bold uppercase border transition-colors ${
                      merchForm.is_coming_soon ? 'bg-[#F97316]/10 border-[#F97316]/30 text-[#F97316]' : 'bg-[#0A0A0A] border-white/10 text-[#71717A]'
                    }`}>Coming Soon</button>
                  <button type="button" onClick={() => setMerchForm({...merchForm, is_visible: !merchForm.is_visible})}
                    className={`flex items-center gap-1.5 px-2 py-1 font-body text-[10px] font-bold uppercase border transition-colors ${
                      merchForm.is_visible ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-[#0A0A0A] border-white/10 text-[#71717A]'
                    }`}>{merchForm.is_visible ? 'Visible' : 'Hidden'}</button>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-3">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Images (up to 4)</Label>
              {(merchForm.images||[]).map((img, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded p-2" data-testid={`merch-img-${idx}`}>
                  {img && <img src={img.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
                  <Input value={img} onChange={(e) => {
                    const imgs = [...merchForm.images];
                    imgs[idx] = e.target.value;
                    setMerchForm({...merchForm, images: imgs});
                  }} placeholder="Image URL" className="bg-transparent border-0 text-white text-xs h-8 focus:ring-0 focus:border-0 p-0 flex-1" />
                  <div className="flex items-center gap-0.5 shrink-0">
                    {idx > 0 && <button type="button" onClick={() => { const imgs=[...merchForm.images]; [imgs[idx-1],imgs[idx]]=[imgs[idx],imgs[idx-1]]; setMerchForm({...merchForm,images:imgs}); }}
                      className="text-[#71717A] hover:text-white p-0.5"><ChevronUp size={12}/></button>}
                    {idx < merchForm.images.length-1 && <button type="button" onClick={() => { const imgs=[...merchForm.images]; [imgs[idx],imgs[idx+1]]=[imgs[idx+1],imgs[idx]]; setMerchForm({...merchForm,images:imgs}); }}
                      className="text-[#71717A] hover:text-white p-0.5"><ChevronDown size={12}/></button>}
                    <button type="button" onClick={() => setMerchForm({...merchForm, images: merchForm.images.filter((_,i)=>i!==idx)})}
                      className="text-[#FF2A2A] hover:text-white p-0.5"><Trash2 size={12}/></button>
                  </div>
                </div>
              ))}
              {(merchForm.images||[]).length < 4 && (
                <div className="flex gap-2">
                <button type="button" data-testid="merch-add-image-btn"
                  onClick={() => setMerchForm({...merchForm, images: [...(merchForm.images||[]), '']})}
                  className="flex-1 bg-[#0A0A0A] border border-dashed border-white/20 rounded py-2 text-center hover:border-[#FF2A2A]/50 transition-colors font-body text-xs text-[#71717A] hover:text-white">
                  + Add Image URL
                </button>
                <label data-testid="merch-upload-image-btn"
                  className="flex-1 cursor-pointer bg-[#0A0A0A] border border-dashed border-white/20 rounded py-2 text-center hover:border-[#FF2A2A]/50 transition-colors font-body text-xs text-[#71717A] hover:text-white">
                  {merchUploading ? 'Uploading...' : '+ Upload Image'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                    data-testid="merch-upload-image-input" disabled={merchUploading} onChange={handleMerchImageUpload} />
                </label>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/5">
            <Button data-testid="merch-cancel-btn" onClick={() => setMerchDialogOpen(false)} variant="outline"
              className="border-white/10 text-white rounded-none hover:bg-white/5 font-body"><X size={14} className="mr-1" /> CANCEL</Button>
            <Button data-testid="merch-save-btn" onClick={handleSaveMerch} disabled={merchSaving || !merchForm.name.trim()}
              className="bg-[#FF2A2A] text-white rounded-none hover:bg-[#E60000] font-body font-bold uppercase tracking-wide disabled:opacity-50 px-6">
              {merchSaving ? <span className="flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving...</span>
                : <span className="flex items-center gap-2"><Save size={14} /> {editingMerchId ? 'UPDATE' : 'CREATE'}</span>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Episode Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#141414] border border-white/10 text-white max-w-lg rounded-none sm:max-w-xl" data-testid="episode-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl uppercase">
              {editingId ? 'EDIT EPISODE' : 'NEW EPISODE'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1 space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Episode #</Label>
                <Input
                  data-testid="episode-number-input"
                  type="number"
                  value={form.episode_number}
                  onChange={(e) => setForm({ ...form, episode_number: parseInt(e.target.value) || 1 })}
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]"
                />
              </div>
              <div className="col-span-2 sm:col-span-1 space-y-2">
                <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Status</Label>
                <div className="flex items-center gap-3 h-10">
                  <button
                    type="button"
                    data-testid="episode-published-toggle"
                    onClick={() => setForm({ ...form, is_published: !form.is_published })}
                    className={`flex items-center gap-2 px-4 py-2 font-body text-xs font-bold uppercase tracking-wider border transition-colors ${
                      form.is_published
                        ? 'bg-[#FF2A2A]/10 border-[#FF2A2A]/30 text-[#FF2A2A]'
                        : 'bg-[#0A0A0A] border-white/10 text-[#71717A]'
                    }`}
                  >
                    {form.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                    {form.is_published ? 'Published' : 'Draft'}
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Title</Label>
              <Input
                data-testid="episode-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Episode title"
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Description</Label>
              <Textarea
                data-testid="episode-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Episode description..."
                rows={3}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none resize-none focus:border-[#FF2A2A]"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">YouTube URL</Label>
              <Input
                data-testid="episode-youtube-input"
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Spotify URL</Label>
              <Input
                data-testid="episode-spotify-input"
                value={form.spotify_url}
                onChange={(e) => setForm({ ...form, spotify_url: e.target.value })}
                placeholder="https://open.spotify.com/episode/..."
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Thumbnail URL (optional)</Label>
              <Input
                data-testid="episode-thumbnail-input"
                value={form.thumbnail_url}
                onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                placeholder="https://..."
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none h-10 focus:border-[#FF2A2A]"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Episode Video</Label>
              {form.video_url ? (
                <div className="bg-[#0A0A0A] border border-white/10 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body text-xs text-[#1DB954] flex items-center gap-1">
                      <Eye size={12} /> Video uploaded
                    </span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, video_url: '' })}
                      className="font-body text-xs text-[#FF2A2A] hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                  <p className="font-body text-xs text-[#71717A] truncate">{form.video_url}</p>
                </div>
              ) : (
                <div className="relative">
                  <input
                    data-testid="episode-video-upload"
                    type="file"
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                    onChange={handleVideoUpload}
                    disabled={uploading}
                    className="absolute inset-0 opacity-0 cursor-pointer z-10"
                  />
                  <div className={`bg-[#0A0A0A] border border-dashed border-white/20 p-4 text-center hover:border-[#FF2A2A]/50 transition-colors ${uploading ? 'opacity-50' : ''}`}>
                    {uploading ? (
                      <p className="font-body text-sm text-[#A1A1AA] flex items-center justify-center gap-2">
                        <Loader2 size={16} className="animate-spin" /> Uploading video...
                      </p>
                    ) : (
                      <p className="font-body text-sm text-[#71717A]">
                        Click to upload video (MP4, MOV, WebM — max 4GB)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Reels from this episode */}
            <div className="space-y-3">
              <Label className="font-body text-xs uppercase tracking-wider text-[#A1A1AA] font-bold">Reels From This Episode (max 3)</Label>
              
              {form.reels.map((reel, idx) => (
                <div key={idx} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3 space-y-2" data-testid={`reel-editor-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-xs font-bold text-[#FF2A2A]">Reel {idx + 1}</span>
                    <div className="flex items-center gap-1">
                      {idx > 0 && (
                        <button type="button" onClick={() => {
                          const r = [...form.reels];
                          [r[idx-1], r[idx]] = [r[idx], r[idx-1]];
                          setForm({...form, reels: r});
                        }} className="text-[#71717A] hover:text-white p-1 text-xs font-bold">UP</button>
                      )}
                      {idx < form.reels.length - 1 && (
                        <button type="button" onClick={() => {
                          const r = [...form.reels];
                          [r[idx], r[idx+1]] = [r[idx+1], r[idx]];
                          setForm({...form, reels: r});
                        }} className="text-[#71717A] hover:text-white p-1 text-xs font-bold">DN</button>
                      )}
                      <button type="button" data-testid={`reel-remove-${idx}`} onClick={() => {
                        setForm({...form, reels: form.reels.filter((_,i) => i !== idx)});
                      }} className="text-[#FF2A2A] hover:text-white p-1">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={reel.platform || 'instagram'}
                      onChange={(e) => {
                        const r = [...form.reels];
                        r[idx] = {...r[idx], platform: e.target.value};
                        setForm({...form, reels: r});
                      }}
                      className="appearance-none bg-[#141414] border border-white/10 text-white font-body text-xs px-2 py-1.5 rounded focus:border-[#FF2A2A] focus:outline-none w-28"
                    >
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                    </select>
                    <Input
                      value={reel.label || ''}
                      onChange={(e) => {
                        const r = [...form.reels];
                        r[idx] = {...r[idx], label: e.target.value};
                        setForm({...form, reels: r});
                      }}
                      placeholder="Short label"
                      className="bg-[#141414] border-white/10 text-white rounded-none h-8 text-xs focus:border-[#FF2A2A] flex-1"
                    />
                  </div>
                  <Input
                    value={reel.link || ''}
                    onChange={(e) => {
                      const r = [...form.reels];
                      r[idx] = {...r[idx], link: e.target.value};
                      setForm({...form, reels: r});
                    }}
                    placeholder="https://www.instagram.com/reel/... or TikTok URL"
                    className="bg-[#141414] border-white/10 text-white rounded-none h-8 text-xs focus:border-[#FF2A2A]"
                  />
                  <Input
                    value={reel.thumbnail || ''}
                    onChange={(e) => {
                      const r = [...form.reels];
                      r[idx] = {...r[idx], thumbnail: e.target.value};
                      setForm({...form, reels: r});
                    }}
                    placeholder="Thumbnail URL (optional)"
                    className="bg-[#141414] border-white/10 text-white rounded-none h-8 text-xs focus:border-[#FF2A2A]"
                  />
                </div>
              ))}

              {form.reels.length < 3 && (
                <button
                  type="button"
                  data-testid="add-reel-btn"
                  onClick={() => setForm({...form, reels: [...form.reels, {platform: 'instagram', link: '', label: '', thumbnail: ''}]})}
                  className="w-full bg-[#141414] border border-dashed border-white/20 rounded-lg py-2.5 text-center hover:border-[#FF2A2A]/50 transition-colors font-body text-xs text-[#71717A] hover:text-white"
                >
                  + Add Reel
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/5">
            <Button
              data-testid="episode-cancel-btn"
              onClick={() => setDialogOpen(false)}
              variant="outline"
              className="border-white/10 text-white rounded-none hover:bg-white/5 font-body"
            >
              <X size={14} className="mr-1" /> CANCEL
            </Button>
            <Button
              data-testid="episode-save-btn"
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="bg-[#FF2A2A] text-white rounded-none hover:bg-[#E60000] font-body font-bold uppercase tracking-wide disabled:opacity-50 px-6"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Saving...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save size={14} /> {editingId ? 'UPDATE' : 'CREATE'}
                </span>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
